import { Box } from '@mui/material';
import { memo, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { useInlineAsset } from '../context';
import { useMediaLibStore } from '../../../common/MediaLibraryUtilities/useMediaLibStore';
import { handleArticleInsertion } from '../../../common/MediaLibraryUtilities/MediaLibraryInsertHooks';
import { tryParseDropEventAsJson } from 'utility/functions';

const InlineWrapper = memo((props: any) => {
  const notify = useNotify();
  const { view, inlineProps, setIsShowButtons } = useInlineAsset();
  const { setSelectedRecords, setReplaceNode } = useMediaLibStore();
  const allowDrop = inlineProps.node.attrs.placeholder;

  // Drop handler switch for handling different drag types - see also: https://tiptap.dev/docs/editor/api/extensions/file-handler
  const handleDropEvent = useCallback(
    (event: React.DragEvent<Element>) => {
      event?.preventDefault();
      if (event?.dataTransfer?.types?.includes('application/json')) {
        try {
          const parsedData = tryParseDropEventAsJson(event);
          switch (parsedData?.dragType) {
            case 'MediaLibAssets': {
              // Prerequisites for handling Media Library ("Asset Library") drops
              if (
                parsedData?.records &&
                Array.isArray(parsedData?.records) &&
                parsedData?.records.length > 0 && // Ensure at least one record is selected
                inlineProps.editor?.schema?.nodes?.bloxInline // Ensure the schema has the bloxInline node
              ) {
                // Call the insertion function with the current drop event
                const wasSuccessful = handleArticleInsertion(
                  inlineProps.editor,
                  parsedData.records,
                  notify,
                  event, // Pass the DragEvent to the handleArticleInsertion
                  inlineProps.getPos(), // Pass the current
                );
                if (wasSuccessful) {
                  setSelectedRecords([]); // Clear selected records in Media Library
                  setReplaceNode(undefined); // Clear the replaceNode in Media Library
                } else {
                  throw new Error('Insertion failed.'); // Throw to catch block.
                }
              }
              break;
            }
            // Add more switch cases here for different drag types if we need to.
            default:
              console.warn('Unhandled application/json drag type:', parsedData?.dragType);
          }
        } catch (error) {
          console.error('Failed to parse dropped data:', error);
        }
      }

      return; // Optional explicit return for clarity
    },
    [inlineProps, notify, setReplaceNode, setSelectedRecords], // Ensure all dependencies are correctly listed here
  );

  return (
    <Box
      data-testid="inline-asset-wrapper"
      className={`blox-inline__${view}_wrapper`}
      onMouseEnter={() => view === 'display' && setIsShowButtons(true)}
      onMouseLeave={() => view === 'display' && setIsShowButtons(false)}
      onDrop={allowDrop ? handleDropEvent : undefined}
      sx={(theme) => ({
        position: 'relative',
        padding: '1rem 1.5rem',
        border: '1px solid #ebebec',
        borderRadius: 1,
        marginBottom: 2.5,
        display: 'grid',
        gap: 2.5, // Add a gap between the two columns
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.grey[100],
        ':hover': {
          backgroundColor: `${
            theme.palette.mode === 'dark'
              ? theme.palette.grey[800]
              : theme.palette.grey[200]
          } !important`,
          color: theme.palette.primary,
          '& .MuiFormHelperText-root': {
            backgroundColor: `${
              theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.grey[200]
            } !important`,
          },
        },
      })}
    >
      {props.children}
    </Box>
  );
});

InlineWrapper.displayName = 'InlineWrapper';
export default InlineWrapper;
